import React from 'react'
import dayjs from 'dayjs';
import AgTechGrid from '@agtech/web/components/Grids/AgTechGrid';
import { AgTechActionButton } from '@agtech/web/components/Buttons/AgTechButtons';
import { gridItems } from '@agtech/web/components/Grids/Data/AgTechGridProps';
import { useAgTechQueryCombination } from '@agtech/core/data/queries/AgTechDataQueries';
import AgTechQueryLoadingSurface from '@agtech/core/data/queries/AgTechQueryLoadingSurface';
import { PlanningTaskDataGetSaleTasksQuery } from '@sales/data/planningTasks/PlanningTaskQueries';
import { OperationDataGetUsersQuery } from '@sales/data/operation/users/OperationUserQueries';
import { useCurrentSale } from '@sales/data/sales/SaleDataStore';
import ApplyTaskTemplateDialog from '../../dialogs/ApplyTaskTemplateDialog';
import AddPlanningTaskDialog from '../../dialogs/AddPlanningTaskDialog';
import PlanningTaskActionMenu from '../menus/PlanningTaskActionMenu';

// AgTechGrid rows are memoized on isRowEntityTheSame alone (see AgTechGridRow.tsx) - a column's
// value function can close over other data (like the users index below), but if that data resolves
// asynchronously *after* a row's own fields already matched, the memoized row never re-renders to
// pick it up. Combining the two queries and baking assignedUserName onto the row itself sidesteps
// that: it becomes part of what isRowEntityTheSame compares, so a change is never missed.
const SalePlanningTaskGrid = () => {
    const currentSale = useCurrentSale();

    const gridDataQuery = useAgTechQueryCombination({
        firstQuery: {
            query: PlanningTaskDataGetSaleTasksQuery,
            queryContext: { saleId: currentSale.id }
        },
        secondQuery: {
            query: OperationDataGetUsersQuery
        },
        projection: (tasks, users) => {
            return Object.values(tasks).map(task => ({
                ...task,
                assignedUserName: task.assignedToUserId && users[task.assignedToUserId]
                    ? `${users[task.assignedToUserId].firstName} ${users[task.assignedToUserId].lastName}`
                    : undefined
            }));
        }
    });

    return (
        <AgTechQueryLoadingSurface query={gridDataQuery}>
            {(tasks) => (
                <AgTechGrid
                    data={{
                        itemQuery: gridItems(tasks)
                    }}
                    header={{
                        title: `Planning Tasks`,
                        content: () => (
                            <div className='column-based gap-x-3 flex'>
                                <AddPlanningTaskDialog.Button saleId={currentSale.id} />
                                <ApplyTaskTemplateDialog.Button saleId={currentSale.id} />
                            </div>
                        )
                    }}
                    columns={[
                        {
                            header: "Task",
                            value: item => item.taskName,
                            classes: 'bg-slate-100 emphasized'
                        },
                        {
                            header: "Description",
                            value: i => i.taskDescription,
                            size: 2
                        },
                        {
                            header: "Priority",
                            value: i => i.priority.toString()
                        },
                        {
                            header: "Start Date",
                            value: i => dayjs(i.startDate).format('MM/DD/YYYY')
                        },
                        {
                            header: "End Date",
                            value: i => dayjs(i.endDate).format('MM/DD/YYYY')
                        },
                        {
                            header: "Status",
                            value: i => i.isComplete ? 'Complete' : 'Incomplete'
                        },
                        {
                            header: "Assigned To",
                            value: i => i.assignedUserName ?? 'Unassigned'
                        }
                    ]}
                    isRowEntityTheSame={(prev, curr) => {
                        return prev.taskName === curr.taskName &&
                            prev.isComplete === curr.isComplete &&
                            prev.assignedToUserId === curr.assignedToUserId &&
                            prev.assignedUserName === curr.assignedUserName &&
                            prev.nextNotificationDate === curr.nextNotificationDate;
                    }}
                    searchConfig={{
                        options: [
                            { field: 'Task', value: i => i.taskName },
                            { field: 'Description', value: i => i.taskDescription }
                        ],
                        placeholder: 'Search planning tasks...'
                    }}
                    noRecordsConfig={{
                        message: 'No planning tasks were found for this sale',
                        action: {
                            prompt: 'Add a task, or apply a task template to get started',
                            button: () => (
                                <div className='column-based gap-x-3 flex'>
                                    <AddPlanningTaskDialog.Button saleId={currentSale.id} classes='button-highlight' />
                                    <ApplyTaskTemplateDialog.Button saleId={currentSale.id} classes='button-highlight' />
                                </div>
                            )
                        }
                    }}
                    actions={{
                        rendering: i => (
                            <AgTechActionButton classes='stretched centered container'>
                                <PlanningTaskActionMenu task={i} />
                            </AgTechActionButton>
                        )
                    }}
                />
            )}
        </AgTechQueryLoadingSurface>
    )
}

export default SalePlanningTaskGrid
